import React, { useState, useRef } from 'react';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../lib/api';
import { toast } from 'sonner';
import { Settings, Camera, User, Phone, Mail, Save, Loader2 } from 'lucide-react';

export default function DoctorSettingsPage() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(user?.profile_photo || null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen geçerli bir görsel seçin');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Dosya boyutu 2MB\'dan küçük olmalı');
      return;
    }

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      setPhotoPreview(base64);
      setUploadingPhoto(true);
      try {
        const result = await authApi.updateProfile({ profile_photo: base64 });
        updateUser({ profile_photo: base64 });
        toast.success('Profil fotoğrafı güncellendi');
      } catch (err) {
        toast.error('Fotoğraf yüklenemedi');
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await authApi.updateProfile({ name, phone });
      updateUser({ name, phone });
      toast.success('Profil güncellendi');
    } catch (err) {
      toast.error('Kayıt başarısız: ' + (err.message || ''));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Ayarlar" subtitle="Profil bilgilerinizi yönetin">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Profil Fotoğrafı */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-gradient-to-r from-[#5A67D8] to-[#4C51BF]">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Profil Fotoğrafı
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center gap-6">
              <div className="relative group">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-slate-100 border-2 border-slate-200 shadow-inner">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Profil" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#5A67D8] to-[#4C51BF]">
                      <span className="text-2xl font-bold text-white">
                        {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U'}
                      </span>
                    </div>
                  )}
                </div>
                {uploadingPhoto && (
                  <div className="absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#5A67D8] text-white flex items-center justify-center shadow-lg hover:bg-[#4C51BF] transition-colors"
                  data-testid="settings-photo-upload-btn"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  data-testid="settings-photo-input"
                />
              </div>
              <div>
                <p className="font-medium text-slate-800">{user?.name || 'Doktor'}</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <p className="text-xs text-slate-400 mt-1">JPG, PNG - Maks 2MB</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profil Bilgileri */}
        <Card className="border-0 shadow-lg overflow-hidden">
          <CardHeader className="py-4 px-6 bg-slate-50 border-b">
            <CardTitle className="text-sm flex items-center gap-2">
              <User className="w-4 h-4 text-[#5A67D8]" />
              Profil Bilgileri
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2 text-sm">
                <User className="w-3.5 h-3.5 text-slate-400" />
                Ad Soyad
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. Adınız Soyadınız"
                data-testid="settings-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-sm">
                <Mail className="w-3.5 h-3.5 text-slate-400" />
                E-posta
              </Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="bg-slate-50 text-slate-500"
              />
              <p className="text-xs text-slate-400">E-posta adresi değiştirilemez</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 text-sm">
                <Phone className="w-3.5 h-3.5 text-slate-400" />
                Telefon
              </Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="0532 XXX XX XX"
                data-testid="settings-phone-input"
              />
            </div>

            <div className="pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#5A67D8] hover:bg-[#4C51BF]"
                data-testid="settings-save-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
